import { ChangeEvent, useEffect, useState } from "react";
import { DealerLocation } from "types/dealer";
import { redBorder } from "constants/index";
import { CustomIcon } from "./customIcon";
import { getStoreHours, parseStoreHours } from "helpers/storeAppointments";
import { convertToAmPM, convertToMilitary } from "helpers/timeAndDateConverter";

interface Props {
  value?: string;
  name: string;
  errorText: string;
  placeholder: string;
  dealerLocation: DealerLocation | null | undefined;
  onChange: any;
  date: string;
  label?: string;
  id?: string;
  required?: boolean;
  isActive: boolean;
}

const CustomTimePicker: React.FC<Props> = ({
  name,
  errorText,
  onChange,
  placeholder,
  dealerLocation,
  date,
  required,
  label,
  id,
  isActive = true,
}) => {
  const [value, setValue] = useState<any>(placeholder);

  let hours!: string;
  if (dealerLocation) {
    hours = getStoreHours(dealerLocation, date);
  }

  let displayValue = "";
  if (
    value &&
    value !== "" &&
    value !== "First Choice Time" &&
    value !== "Second Choice Time"
  ) {
    displayValue = convertToAmPM(value);
  }
  // // min: store open time, max: store close time.
  // // If user selects outside min & max range the store is closed we show error
  const { min, max } = parseStoreHours(hours);

  function createSyntheticChangeEvent() {
    if (value) {
      const inputElement = document.createElement("input");
      inputElement.type = "time";
      inputElement.name = name;
      inputElement.value = value;

      const syntheticEvent = new CustomEvent("change", {
        bubbles: true,
        cancelable: true,
        detail: { target: inputElement },
      }) as unknown as ChangeEvent<HTMLInputElement>;

      Object.defineProperty(syntheticEvent, "target", {
        get: () => inputElement,
      });

      onChange(syntheticEvent);
    }
  }

  useEffect(() => {
    if (value) {
      createSyntheticChangeEvent();
    }
  }, [value]);

  const allTime = [];
  var appointmentHours, minutes, ampm;
  for (var i = 0; i <= 1440; i += 15) {
    appointmentHours = Math.floor(i / 60);
    minutes = i % 60;
    if (minutes < 10) {
      minutes = "0" + minutes; // adding leading zero
    }
    ampm = appointmentHours % 24 < 12 ? "AM" : "PM";
    appointmentHours = appointmentHours % 12;
    if (appointmentHours === 0) {
      appointmentHours = 12;
    }
    allTime.push(appointmentHours + ":" + minutes + " " + ampm);
  }

  function filterTimeRange(time: string[], min: string, max: string) {
    return time.filter(
      (time) => convertToMilitary(time) >= min && convertToMilitary(time) <= max
    );
  }
  const times: string[] = filterTimeRange(allTime, min, max);

  const handleTimeChange = (event: any) => {
    const selectedTime = event.target.value;
    setValue(selectedTime);
  };

  return (
    <div className="ta-form-group ta-w-full">
      {label && (
        <label
          htmlFor={id}
          className="ta-text-sm ta-mb-1 ta-block ta-font-bold ta-text-black"
        >
          {label}
          {required && <span className="ta-text-red">*</span>}
        </label>
      )}
      <div
        className={`ta-border ta-border-[#8F8F8F] ta-rounded-[3px] ta-h-[33px] ta-w-full ta-flex ta-items-center ta-relative ta-p-2 ${
          required && !value ? redBorder : ""
        }`}
      >
        <input
          disabled={!isActive}
          className="ta-text-[0.67rem] ta-w-full"
          type="text"
          name={name}
          value={displayValue}
          placeholder={placeholder}
          readOnly
        />
        {required && !value && (
          <span className="ta-sr-only" role="alert">
            {errorText}
          </span>
        )}
        <select
          className="ta-appearance-none ta-w-full ta-text-[0.67rem] ta-bg-white ta-opacity-0 ta-absolute ta-h-full ta-left-0 ta-z-[1]"
          value={value}
          disabled={!isActive}
          onChange={handleTimeChange}
          placeholder={placeholder}
          aria-label={label}
          aria-required={required}
          aria-invalid={required && !value}
          id={id}
        >
          {times.map((time) => (
            <option key={time} value={convertToMilitary(time)}>
              {convertToAmPM(time)}
            </option>
          ))}
        </select>
        <div>
          <CustomIcon />
        </div>
      </div>
    </div>
  );
};
export default CustomTimePicker;
